import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Modal,
  Select,
  Skeleton,
  Switch,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  getEinvoiceReminderCandidates,
  getEinvoiceSummaryPreview,
  saveEinvoiceReminderSettings,
} from '@/services/einvoiceReminders.service';

const fmt = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '—');

/**
 * The internal summary of clients with open e-invoices (Bora, 18.10.2026):
 * its own switch, who receives it (HLB staff, e.g. Milka) and a preview of
 * the mail as it would go out on the next summary day.
 */
export default function EinvoiceSummarySettings({ data }) {
  const { t } = useTranslation('einvoiceReminders');
  const queryClient = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: candidates = [] } = useQuery({
    queryKey: ['einvoice-reminder-candidates'],
    queryFn: getEinvoiceReminderCandidates,
    enabled: !!data.canManage,
    staleTime: 10 * 60 * 1000,
  });
  const preview = useQuery({
    queryKey: ['einvoice-summary-preview'],
    queryFn: () => getEinvoiceSummaryPreview(),
    enabled: previewOpen,
  });

  const save = useMutation({
    mutationFn: saveEinvoiceReminderSettings,
    onSuccess: (d) => {
      queryClient.setQueryData(['einvoice-reminder-settings'], d);
      message.success(t('summary.saved'));
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const options = [...candidates, ...data.summaryRecipients]
    .filter((u, i, all) => all.findIndex((x) => x._id === u._id) === i)
    .map((u) => ({
      value: u._id,
      label: u.name ? `${u.name} (${u.email})` : u.email,
    }));

  return (
    <div className="grid gap-2 rounded-lg border border-zinc-200 p-3">
      <div className="flex items-center justify-between gap-3">
        <b>{t('summary.title')}</b>
        <Switch
          checked={data.summaryEnabled}
          disabled={
            !data.canManage ||
            (!data.summaryEnabled && !data.summaryRecipients.length)
          }
          loading={save.isPending}
          onChange={(summaryEnabled) => save.mutate({ summaryEnabled })}
        />
      </div>
      <div className="text-xs opacity-70">{t('summary.rule')}</div>
      <div className="flex flex-wrap items-center gap-1 text-xs">
        <span className="opacity-70">{t('settings.schedule')}:</span>
        {data.summarySchedule.map((d) => (
          <Tag
            key={d.date}
            color={d.date === data.nextSummaryDay?.date ? 'blue' : undefined}
            className="m-0"
          >
            {fmt(d.date)}
          </Tag>
        ))}
      </div>
      <div className="grid gap-1">
        <span className="text-xs opacity-70">{t('summary.recipients')}</span>
        <Select
          size="small"
          mode="multiple"
          showSearch
          optionFilterProp="label"
          disabled={!data.canManage}
          value={data.summaryRecipients.map((u) => u._id)}
          options={options}
          placeholder={t('summary.recipientsPlaceholder')}
          onChange={(ids) => save.mutate({ summaryRecipients: ids })}
          style={{ width: '100%' }}
        />
      </div>
      <div className="text-xs">
        {data.summaryEnabled ? t('summary.stateOn') : t('summary.stateOff')}
      </div>
      <div className="flex justify-end">
        <Button
          size="small"
          shape="round"
          icon={<EyeOutlined />}
          onClick={() => setPreviewOpen(true)}
          disabled={!data.canManage}
        >
          {t('summary.preview')}
        </Button>
      </div>
      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={820}
        title={t('summary.previewTitle')}
        destroyOnClose
      >
        {preview.isLoading && <Skeleton active />}
        {preview.error && (
          <Alert
            type="error"
            showIcon
            className="!static"
            message={
              preview.error?.response?.data?.message || preview.error.message
            }
          />
        )}
        {preview.data && (
          <div className="grid gap-2">
            <div className="text-sm">
              <span className="opacity-60">{t('mail.subject')}:</span>{' '}
              <b>{preview.data.subject}</b>
              <span className="opacity-60">
                {' '}
                · {fmt(preview.data.sendDay?.date)}
              </span>
            </div>
            <iframe
              title="summary"
              sandbox=""
              srcDoc={preview.data.html}
              style={{
                width: '100%',
                height: '65vh',
                border: '1px solid rgba(0,0,0,.1)',
                borderRadius: 8,
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
